import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/components/theme-provider";
import { DensityProvider } from "@/hooks/useDensity";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import Layout from "@/components/Layout";
import PrivateRoute from "@/components/PrivateRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { CommandPaletteRoot } from "@/components/CommandPalette";
import { queryClient } from "@/lib/queryClient";

import LoginPage from "@/pages/LoginPage";
import OAuthCallbackPage from "@/pages/OAuthCallbackPage";
import NotFound from "@/pages/NotFound";
import GettingStartedPage from "@/pages/GettingStartedPage";

// Five EDR surfaces
import PosturePage from "@/pages/security/PosturePage";
import TriagePage from "@/pages/security/TriagePage";
import InvestigatePage from "@/pages/security/InvestigatePage";
import CoveragePage from "@/pages/security/CoveragePage";
import FleetPage from "@/pages/security/FleetPage";
import IocsPage from "@/pages/security/IocsPage";

// Four Lighthouse surfaces
import LhPosturePage from "@/pages/lighthouse/LhPosturePage";
import LhApprovalsPage from "@/pages/lighthouse/LhApprovalsPage";
import LhInventoryPage from "@/pages/lighthouse/LhInventoryPage";
import LhControlsPage from "@/pages/lighthouse/LhControlsPage";

import SettingsPage from "@/pages/settings/SettingsPage";
import SystemMcpPage from "@/pages/system/SystemMcpPage";

import "./App.css";

/** Legacy paths → new surfaces. Kept for one release. */
const REDIRECTS: Array<[string, string]> = [
  ["dashboard", "/posture"],
  ["security/alerts", "/triage"],
  ["security/investigations", "/triage"],
  ["security/events", "/investigate"],
  ["security/hunts", "/investigate?tab=hunts"],
  ["security/hunts/:huntId", "/investigate?tab=hunts"],
  ["security/sigma", "/coverage"],
  ["security/detections", "/coverage"],
  ["security/mitre", "/coverage"],
  ["security/threat-intel", "/coverage"],
  ["security/persistence", "/investigate?tab=persistence"],
  ["security/devices", "/fleet"],
  ["security/quarantine", "/fleet"],
  ["security/ai-guard", "/fleet"],
  ["security/compliance", "/fleet"],
  ["security/mcp-discovery", "/ai/inventory"],
  ["security/external-sources", "/settings"],
  ["security/audit", "/settings"],
  ["security/settings", "/settings"],
  ["lighthouse/agents", "/ai/inventory"],
  ["lighthouse/agents/:agentId", "/ai/inventory"],
  ["lighthouse/approvals", "/ai/approvals"],
  ["lighthouse/policies", "/ai/controls"],
  ["lighthouse/publishers", "/ai/inventory"],
  ["lighthouse/attestations", "/ai/inventory"],
  ["lighthouse/risk", "/ai/posture"],
  ["lighthouse/identities", "/ai/inventory"],
  ["lighthouse/cost", "/ai/controls"],
  ["lighthouse/egress", "/ai/controls"],
  ["lighthouse/connectors", "/settings"],
  ["lighthouse/mcp-servers", "/ai/controls"],
  ["system/enrollment-codes", "/settings"],
  ["system/users", "/settings"],
  ["system/sso", "/settings"],
  ["system/tokens", "/settings"],
  ["system/webhooks", "/settings"],
  ["system/siem", "/settings"],
  ["system/notifications", "/settings"],
  ["system/tenant", "/settings"],
];

function App() {
  return (
    <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
      <DensityProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <NotificationProvider>
              <ErrorBoundary>
                <Router>
                  <CommandPaletteRoot />
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/oauth/callback" element={<OAuthCallbackPage />} />

                    <Route
                      path="/"
                      element={
                        <PrivateRoute>
                          <Layout />
                        </PrivateRoute>
                      }
                    >
                      <Route index element={<Navigate to="/posture" replace />} />
                      <Route path="getting-started" element={<GettingStartedPage />} />

                      {/* Security (EDR) — five surfaces */}
                      <Route path="posture" element={<PosturePage />} />
                      <Route path="triage" element={<TriagePage />} />
                      <Route path="investigate" element={<InvestigatePage />} />
                      <Route path="coverage" element={<CoveragePage />} />
                      <Route path="fleet" element={<FleetPage />} />
                      <Route path="security/iocs" element={<IocsPage />} />

                      {/* AI Governance (Lighthouse) — four surfaces */}
                      <Route path="ai/posture" element={<LhPosturePage />} />
                      <Route path="ai/approvals" element={<LhApprovalsPage />} />
                      <Route path="ai/inventory" element={<LhInventoryPage />} />
                      <Route path="ai/controls" element={<LhControlsPage />} />

                      {/* Settings */}
                      <Route path="settings" element={<SettingsPage />} />
                      <Route path="settings/:tab" element={<SettingsPage />} />

                      {/* System */}
                      <Route path="system/mcp" element={<SystemMcpPage />} />

                      {/* Legacy redirects */}
                      {REDIRECTS.map(([from, to]) => (
                        <Route key={from} path={from} element={<Navigate to={to} replace />} />
                      ))}
                    </Route>

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Router>
                <Toaster />
                <Sonner />
              </ErrorBoundary>
            </NotificationProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </DensityProvider>
    </ThemeProvider>
  );
}

export default App;
